import {
  Avatar,
  Badge,
  Button,
  Flex,
  Td,
  Text,
  Tr,
  useColorModeValue,
  IconButton,
  useToast,
  Select,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  ModalFooter,
  Spinner
} from "@chakra-ui/react";
import React, { useState, useRef, useEffect } from "react";
import { CloseIcon, AddIcon } from "@chakra-ui/icons";
import productService from "../../api/products/products";
import categorieService from "../../api/categories/categories";

function TablesProductRow(props) {
  const { id, image, name, categorie, categorieId, features, createdAt, desc, domain, status, date, categories } = props;
  const textColor = useColorModeValue("gray.700", "white");
  const toast = useToast();

  // State for modal and form inputs
  const [isOpen, setIsOpen] = useState(false);
  const [productName, setProductName] = useState(name);
  const [selectedCategory, setSelectedCategory] = useState(categorieId);
  const [description, setDescription] = useState(desc || "");  // Assuming you have a description field
  const [featureList, setFeatureList] = useState(features || []);
  const [categs, setCategs] = useState([]);
  const [images, setImage] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    // Fonction pour récupérer les produits
    const fetchProducts = async () => {
      try {
        setLoading(true); // Activer l'état de chargement
        const response = await categorieService.getCategories();
        setCategs(response); // Stocker les données récupérées
      } catch (err) {
        console.error("Erreur lors de la récupération des produits :", err);
        setError(err.message || "Une erreur s'est produite.");
      } finally {
        setLoading(false); // Désactiver l'état de chargement
      }
    };

    fetchProducts(); // Appeler la fonction de récupération
  }, []);

  // Create a ref for initial focus
  const initialRef = useRef();

  const handleOpenModal = () => {
    setIsOpen(true);
  };

  const handleCloseModal = () => {
    setIsOpen(false);
  };

  const handleDelete = async () => {
    const isConfirmed = window.confirm(`Êtes-vous sûr de vouloir supprimer le produit "${name}" ?`);

    if (isConfirmed) {
      try {
        await productService.deleteProduct(id);
        toast({
          title: "Produit supprimé.",
          description: "Le produit a été supprimé avec succès.",
          status: "success",
          duration: 5000,
          isClosable: true,
        });
        window.location.reload()
      } catch (error) {
        toast({
          title: "Erreur.",
          description: "Une erreur est survenue lors de la suppression du produit.",
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      }
    }
  };

  const handleEdit = async () => {
    setLoading(true);
    const formData = new FormData();

    // Ajouter les données du produit à formData
    formData.append("name", productName);
    formData.append("category", selectedCategory);
    formData.append("description", description);
    formData.append("features", JSON.stringify(featureList));

    // Ajouter l'image (si une image est sélectionnée)
    if (images) {
      formData.append("photo", images);
    }

    try {
      await productService.updateProduct(id, formData); // Assurez-vous que votre service gère les requêtes multipart/form-data
      toast({
        title: "Produit modifié.",
        description: "Les informations du produit ont été mises à jour.",
        status: "success",
        duration: 5000,
        isClosable: true,
      });
      handleCloseModal();
      window.location.reload();
    } catch (error) {
      toast({
        title: "Erreur.",
        description: "Une erreur est survenue lors de la mise à jour du produit.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };


  return (
    <>
      <Tr>
        <Td minWidth={{ sm: "250px" }} pl="0px">
          <Flex align="center" py=".8rem" minWidth="100%" flexWrap="nowrap">
            <Avatar src={image} w="50px" borderRadius="12px" me="18px" />
            <Flex direction="column">
              <Text fontSize="md" color={textColor} fontWeight="bold" minWidth="100%">
                {name}
              </Text>
            </Flex>
          </Flex>
        </Td>

        <Td>
          <Flex direction="column">
            <Text fontSize="md" color={textColor} fontWeight="bold">
              {categorie}
            </Text>
          </Flex>
        </Td>
        <Td>
          <Text fontSize="md" color={textColor} fontWeight="bold" pb=".5rem">
            {createdAt}
          </Text>
        </Td>
        <Td>
          <Button p="0px" bg="transparent" variant="no-hover" onClick={handleOpenModal}>
            <Text fontSize="md" color="gray.400" fontWeight="bold" cursor="pointer">
              Edit
            </Text>
          </Button>
        </Td>
        <Td>
          <IconButton
            colorScheme="red"
            icon={<CloseIcon />}
            onClick={handleDelete}
            aria-label="Supprimer"
          />
        </Td>
      </Tr>

      {/* Modal for Editing Product */}
      <Modal initialFocusRef={initialRef} isOpen={isOpen} onClose={handleCloseModal}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Modifier le Produit</ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            <FormControl mb={4}>
              <FormLabel>Nom du produit</FormLabel>
              <Input
                ref={initialRef} // This will focus on the product name input when modal opens
                placeholder="Nom du produit"
                value={productName}
                onChange={(e) => setProductName(e.target.value)}
              />
            </FormControl>
            <FormControl mb={4}>
              <FormLabel>Image du produit</FormLabel>
              <Input
                type="file"
                accept="image/*"
                onChange={(e) => setImage(e.target.files[0])}
              />
            </FormControl>
            <FormControl mb={4}>
              <FormLabel>Catégorie</FormLabel>
              {/* Afficher un spinner si le chargement est en cours */}
              <Select
                placeholder="Choisissez une catégorie"
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)} // Mettre à jour la catégorie sélectionnée
              >
                {(categs || []).map((category) => (
                  <option key={category.id} value={category.id}>
                    {category.name}
                  </option>
                ))}
              </Select>
            </FormControl>

            <FormControl mb={4}>
              <FormLabel>Description</FormLabel>
              <Textarea
                placeholder="Description du produit"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
              />
            </FormControl>
            <FormControl mb={4}>
              <FormLabel>Caractéristiques</FormLabel>
              {featureList.map((feature, index) => (
                <Flex key={index} alignItems="center" mb={2}>
                  <Input
                    placeholder={`Caractéristique ${index + 1}`}
                    value={feature.value}
                    onChange={(e) => {
                      const updatedFeatures = [...featureList];
                      updatedFeatures[index] = e.target.value;
                      setFeatureList(updatedFeatures);
                    }}
                  />
                  {featureList.length > 1 && (
                    <IconButton
                      ml={2}
                      size="sm"
                      colorScheme="red"
                      icon={<CloseIcon />}
                      onClick={() => {
                        const updatedFeatures = featureList.filter((_, i) => i !== index);
                        setFeatureList(updatedFeatures);
                      }}
                    />
                  )}
                </Flex>
              ))}
              <IconButton
                aria-label="Ajouter une caractéristique"
                onClick={() => setFeatureList([...featureList, ""])}
                icon={<AddIcon />}
              />
            </FormControl>
          </ModalBody>

          <ModalFooter>
            <Button colorScheme="orange" mr={3} onClick={handleEdit} isLoading={loading}>
              Modifier
            </Button>
            <Button onClick={handleCloseModal}>Annuler</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
}

export default TablesProductRow;
